// Sign in (app flow §2; the API's W1): Apple, Google or an emailed code. No passwords. Apple and
// Google go by redirect (POST /v1/auth/sign-in/social, then to the provider, then back here); a
// provider this server hasn't set up (503 provider-not-configured) is shown disabled with one quiet
// line. The code is one real input under six drawn boxes (CodeInput), so paste, autofill from Mail
// and screen readers just work. The two steps turn like pages (Steps).
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { API_URL } from "../api/client";
import { AuthFrame } from "../components/AuthFrame";
import { Button } from "../components/Button";
import { CodeInput } from "../components/CodeInput";
import { Icon } from "../components/Icon";
import { ProviderButton, providerNoteClass, unavailableNote } from "../components/ProviderButtons";
import ui from "../components/ui.module.css";
import { ApiError } from "../data/problem";
import { refreshSession, useAuthProviders, useSource } from "../data/queries";
import type { SocialProvider } from "../data/types";
import { useTitle } from "../lib/useTitle";
import { Steps } from "../motion/Steps";
import s from "./auth.module.css";

type Search = { next?: string; error?: string };

export const Route = createFileRoute("/sign-in")({
  validateSearch: (q: Record<string, unknown>): Search => ({
    ...(typeof q.next === "string" && q.next.startsWith("/") && !q.next.startsWith("//") ? { next: q.next } : {}),
    ...(typeof q.error === "string" && q.error.length < 100 ? { error: q.error } : {}),
  }),
  component: SignIn,
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND = 30;

/** Better Auth's error codes on the way back from a provider, in words. */
function returnedError(code: string): string {
  if (/access_denied|cancel/i.test(code)) return "Sign-in was cancelled. Try again whenever you’re ready.";
  if (/account_not_linked|email_doesn/i.test(code)) return "That account isn’t linked to a Fellowship account yet. Sign in with your email code, then link it in Settings.";
  return "That sign-in didn’t finish. Try again, or use an email code.";
}

function SignIn() {
  useTitle("Sign in");
  const { next, error: returned } = Route.useSearch();
  const source = useSource();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: providers } = useAuthProviders();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [opening, setOpening] = useState<SocialProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refusals, setRefusals] = useState(0);
  const [socialError, setSocialError] = useState<string | null>(returned ? returnedError(returned) : null);
  const [off, setOff] = useState<Partial<Record<SocialProvider, boolean>>>({});
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const available = { google: (providers?.google ?? true) && !off.google, apple: (providers?.apple ?? true) && !off.apple };
  const note = providers ? unavailableNote(available) : null;

  const social = async (provider: SocialProvider) => {
    setOpening(provider);
    setSocialError(null);
    try {
      const back = `${location.origin}${next ?? "/home"}`;
      const url = await source.startSocial(provider, { callbackURL: back, errorCallbackURL: `${location.origin}/sign-in` });
      window.location.assign(url);
    } catch (e) {
      setOpening(null);
      if (e instanceof ApiError && e.type === "provider-not-configured") setOff((o) => ({ ...o, [provider]: true }));
      else setSocialError((e as Error).message);
    }
  };

  const send = async () => {
    if (!EMAIL.test(email.trim())) {
      setError("That doesn’t look like an email address yet. Try one like name@example.com.");
      emailRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await source.sendCode(email.trim());
    } catch (e) {
      setBusy(false);
      return setError((e as Error).message);
    }
    setBusy(false);
    setCode("");
    setStep("code");
    setResendIn(RESEND);
    setTimeout(() => codeRef.current?.focus(), 480);
  };

  const verify = async (value: string) => {
    setBusy(true);
    setError(null);
    try {
      const { isNew } = await source.verifyCode(email.trim(), value);
      await refreshSession(qc, source);
      setBusy(false);
      setDone(true);
      // A beat for the boxes to take the rubric, then on.
      setTimeout(() => void navigate({ to: isNew ? "/welcome" : (next ?? "/home") }), matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 420);
    } catch (e) {
      setError((e as Error).message || "That code didn’t work. Check the newest email, or send a new code.");
      setRefusals((n) => n + 1);
      setCode("");
      setBusy(false);
      setTimeout(() => codeRef.current?.focus(), 50);
    }
  };

  return (
    <AuthFrame
      verse={{ text: "Two are better than one, because they have a good reward for their labor.", ref: "Ecclesiastes 4:9 · WEB" }}
      top={
        step === "code" ? (
          <button type="button" className={s.backLink} onClick={() => (setStep("email"), setCode(""), setError(null))}>
            <Icon name="back" size={18} />
            Back
          </button>
        ) : undefined
      }
      foot={
        <>
          By continuing you agree to the <Link to="/rules">community rule</Link> and the <a href="https://biblestandard.app/privacy">privacy notice</a>.
        </>
      }
    >
      <Steps step={step} direction={step === "code" ? 1 : -1}>
        {step === "email" ? (
          <>
            <h1 className={s.question}>Sign in to Fellowship</h1>
            <p className={s.lede}>With Apple, Google, or a code we email you. No password to remember, and new readers start here too.</p>
            <div className={s.providers}>
              <ProviderButton provider="apple" available={available.apple} busy={opening === "apple"} onClick={() => void social("apple")} describedBy="providers-note" />
              <ProviderButton provider="google" available={available.google} busy={opening === "google"} onClick={() => void social("google")} describedBy="providers-note" />
              {note && (
                <p id="providers-note" className={providerNoteClass}>
                  {note}
                </p>
              )}
              {socialError && (
                <p className={ui.error} role="alert">
                  {socialError}
                </p>
              )}
            </div>
            <p className={s.or}>
              <span>or with email</span>
            </p>
            <form
              className={s.form}
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <div>
                <label htmlFor="email" className={ui.label}>
                  Email
                </label>
                <input
                  ref={emailRef}
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder="name@example.com"
                  className={`${ui.field} ${s.big}`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  aria-invalid={!!error}
                  aria-describedby="email-error"
                />
                <p id="email-error" className={ui.error} role="alert">
                  {error}
                </p>
              </div>
              <Button type="submit" variant="primary" size="lg" wide busy={busy} disabled={busy} roll={false}>
                {busy ? "Sending your code…" : "Email me a code"}
              </Button>
            </form>
          </>
        ) : (
          <>
            <span className={s.envelope} aria-hidden="true">
              <Icon name="email" size={24} />
            </span>
            <h1 className={s.question}>Check your email</h1>
            <p className={s.lede}>
              We sent a six-digit code to <strong className={s.address}>{email}</strong>. It works for 10 minutes.
            </p>
            <form
              className={s.form}
              onSubmit={(e) => {
                e.preventDefault();
                if (code.length === 6) void verify(code);
              }}
            >
              <CodeInput
                ref={codeRef}
                value={code}
                onChange={(v) => {
                  setCode(v);
                  if (error) setError(null);
                }}
                onComplete={(v) => void verify(v)}
                busy={busy}
                state={done ? "done" : "idle"}
                invalid={!!error}
                errorKey={refusals}
                describedBy="code-error code-hint"
              />
              <p id="code-hint" className={s.hint} aria-live="polite">
                {done ? (
                  <>
                    <Icon name="check" size={16} /> Signed in. One moment…
                  </>
                ) : busy ? (
                  "Checking the code…"
                ) : API_URL ? (
                  "Paste it, or type the six digits."
                ) : (
                  "Any six digits work in this preview."
                )}
              </p>
              <p id="code-error" className={ui.error} role="alert">
                {error}
              </p>
              <div className={s.resend}>
                {resendIn > 0 ? (
                  <span className={s.timer}>
                    <svg viewBox="0 0 20 20" aria-hidden="true" className={s.ring}>
                      <circle cx="10" cy="10" r="8" pathLength={RESEND} style={{ strokeDashoffset: RESEND - resendIn }} />
                    </svg>
                    Send again in {resendIn}s
                  </span>
                ) : (
                  <button type="button" className={ui.text} onClick={() => void send()}>
                    Send a new code
                  </button>
                )}
                <button type="button" className={s.quiet} onClick={() => (setStep("email"), setCode(""), setError(null))}>
                  Use a different email
                </button>
              </div>
            </form>
          </>
        )}
      </Steps>
    </AuthFrame>
  );
}
